import React, { useEffect, useState, useRef } from "react";
import { fireStore } from "../../firebaseConfig";
import { collection, getDocs, query, where } from "firebase/firestore";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const FMFDCoaching = () => {
  const threeDaysAgo = new Date();
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 1);

  const [startDate, setStartDate] = useState(threeDaysAgo.getTime()); // Store as milliseconds
  const [endDate, setEndDate] = useState(Date.now()); // Store as milliseconds

  const [FMCoachings, setFMCoachings] = useState([]);
  const [filteredFMCoachings, setFilteredFMCoachings] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTerritory, setSelectedTerritory] = useState("");

  // Fetch Coachings...
  useEffect(() => {
    const fetchFMCoachings = async () => {
      try {
        const collectionRef = collection(fireStore, "FMCoachings");
        const q = query(
          collectionRef,
          where("id", ">=", startDate),
          where("id", "<=", endDate)
        );
        const querySnapshot = await getDocs(q);

        const FMCoachingsList = querySnapshot.docs.map((doc) => {
          return {
            id: doc.id,
            ...doc.data(),
          };
        });

        const sortedCoachingList = FMCoachingsList.sort((a, b) => b.id - a.id);
        setFMCoachings(sortedCoachingList);
        setFilteredFMCoachings(sortedCoachingList);
        // console.log(FMCoachingsList);
      } catch (error) {
        console.error("Error fetching FMCoachings:", error);
      }
    };

    fetchFMCoachings();
  }, [startDate, endDate]);

  // Fetch Territories...
  useEffect(() => {
    const fetchTerritories = async () => {
      try {
        const collectionRef = collection(fireStore, "Territories");
        const querySnapshot = await getDocs(collectionRef);
        const territoriesList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort territories in ascending order based on a property, e.g., 'name'
        const sortedTerritories = territoriesList.sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });

        setTerritories(sortedTerritories);
      } catch (error) {
        console.error("Error fetching territories:", error);
      }
    };

    fetchTerritories();
  }, []);

  // Export PDF...
  const exportToPDF = async () => {
    const doc = new jsPDF("p", "pt", "a4");
    const { pageSize } = doc.internal;
    const pageW = pageSize.getWidth();
    const pageH = pageSize.getHeight();
    let y = 0;

    for (const item of filteredFMCoachings) {
      const el = document.getElementById(`Coaching-${item.id}`);
      if (!el) continue;

      // Lower scale = faster & lighter
      const canvas = await html2canvas(el, { scale: 1 });
      // JPEG gives much smaller files than PNG
      const img = canvas.toDataURL("image/jpeg", 0.6);
      const imgH = (canvas.height * pageW) / canvas.width;

      if (y + imgH > pageH) {
        doc.addPage();
        y = 0;
      }
      doc.addImage(img, "JPEG", 0, y, pageW, imgH);
      y += imgH;
    }

    doc.save("Coachings.pdf");
  };

  // Territory Filters
  useEffect(() => {
    const filtered = FMCoachings.filter(
      (visit) =>
        visit.user?.territory.name.toLowerCase() ===
        selectedTerritory.toLocaleLowerCase()
    );

    setFilteredFMCoachings(filtered);
    if (selectedTerritory === "All") {
      setFilteredFMCoachings(FMCoachings);
    }
  }, [selectedTerritory]);

  //Search Filter...
  useEffect(() => {
    const filtered = FMCoachings.filter((visit) =>
      visit.user.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredFMCoachings(filtered);
    if (searchQuery === "All") {
      setFilteredFMCoachings(FMCoachings);
    }
  }, [searchQuery]);

  return (
    <div className="p-4 bg-lightgray h-full overflow-y-scroll">
      <div className="bg-white px-4 py-2 rounded-lg shadow-lg max-w-5xl mx-auto mt-2 lg:sticky top-0 z-50">
        <div className="flex flex-wrap justify-between items-center py-2">
          <h1 className="text-lg font-semibold text-liquidblue">
            FM/FD Coaching
          </h1>

          <button
            onClick={exportToPDF}
            className="border border-darkteal font-bold bg-transparent transition duration-300 ease-in-out hover:bg-liquidblue hover:text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-liquidblue focus:ring-opacity-50 active:bg-darkteal active:text-white active:shadow-md text-darkteal rounded-lg px-3 py-1"
          >
            PDF
          </button>
        </div>
        <div className="flex flex-wrap justify-between items-center mb-2 space-y-2 md:space-y-0">
          <select
            id="territories"
            className="w-full md:w-auto border border-gray-300 rounded-lg px-5 py-2 focus:ring focus:ring-blue-300"
            onChange={(e) => setSelectedTerritory(e.target.value)}
            value={selectedTerritory}
          >
            <option value="All">Select Territory</option>
            {territories.map((territory) => (
              <option key={territory.id} value={territory.name}>
                {territory.name}
              </option>
            ))}
          </select>

          <div className="flex items-center">
            {/* From Date Picker */}
            <label
              htmlFor="from-date"
              className="mr-2 font-lato text-liquidblue font-semibold"
            >
              From Date
            </label>
            <input
              type="date"
              id="from-date"
              className="flex-grow border p-2 rounded focus:ring focus:ring-blue-300"
              value={new Date(startDate).toISOString().split("T")[0]} // Convert timestamp to YYYY-MM-DD format
              onChange={(e) => setStartDate(new Date(e.target.value).getTime())} // Convert selected date to timestamp
            />
          </div>
        </div>

        <div className="flex flex-wrap justify-between items-center mb-4 space-y-2 md:space-y-0">
          <input
            type="text"
            placeholder="Search"
            className="w-full md:w-auto border border-gray-300 rounded-lg px-5 py-2 focus:ring focus:ring-blue-300"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="flex items-center ">
            <label
              htmlFor="from-date"
              className="mr-2 font-lato text-liquidblue font-semibold"
            >
              From Date
            </label>
            <input
              type="date"
              id="from-date"
              className="flex-grow border p-2 rounded focus:ring focus:ring-blue-300"
              value={new Date(endDate).toISOString().split("T")[0]} // Convert timestamp to YYYY-MM-DD format
              onChange={(e) => setEndDate(new Date(e.target.value).getTime())} // Convert selected date to timestamp
            />
          </div>
        </div>
      </div>

      {filteredFMCoachings.map((item) => (
        <div
          id={`Coaching-${item.id}`}
          key={item.id}
          className="bg-white p-6 rounded-lg shadow-lg max-w-5xl mx-auto mt-4"
        >
          <h1 className="text-xl font-semibold py-4 text-liquidblue">
            {item.user?.territory?.name}
          </h1>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="flex justify-around">
                  <th className="py-2 px-4 border-b border-gray-200 text-left">
                    {item.user?.name}
                  </th>
                  <th className="py-2 px-4 border-b border-gray-200 text-left">
                    {item.user?.phone}
                  </th>

                  <th className="py-2 px-4 text-left">
                    {new Date(Number(item.id)).toLocaleDateString("en-GB")}{" "}
                    {new Date(Number(item.id)).toLocaleTimeString("en-GB", {
                      hour12: true,
                    })}{" "}
                  </th>
                  <th className="py-2 px-4 border-b border-gray-200 text-left">
                    {item.user?.territory?.name ? (
                      <h1 className="border border-darkteal font-bold bg-transparent transition duration-300 ease-in-out hover:bg-liquidblue hover:text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-liquidblue focus:ring-opacity-50 active:bg-darkteal active:text-white active:shadow-md text-darkteal rounded-lg px-4 py-1">
                        {item.user.territory.name}
                      </h1>
                    ) : null}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="flex flex-row w-full">
                  <td className="py-2 px-4 border-b border-gray-200 flex-1">
                    <div className="space-y-6">
                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">objectivesPlanned</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.objectivesPlanned}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">meetingTime</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.meetingTime}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Banner</span>
                        <div class="dark:bg-white">
                          <label className="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.banner}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">hostFarmer</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.hostFarmer}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">products</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.products}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">siteSelection</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.siteSelection}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">literature</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.literature}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">sitting Arrangement</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.sittingArrange}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">liveSpecimen</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.liveSpecimen}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Quality Participants</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.qualityParticipants}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">fsodress</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.fsodress}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">numberOfParticipants</span>
                        <span className=" font-lato font-bold">
                          {item.numberOfParticipants}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">introduction</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.introduction}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">objectives</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.objectives}
                            />
                          </label>
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato font-bold text-teal-600">
                          Host Farmer
                        </span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.hostFarmer}
                            />
                          </label>
                        </div>
                      </div>
                      {item.hostFarmer && (
                        <div className="flex items-center justify-between gap-4">
                          <span className=" font-lato">
                            Host Farmer's Phone Number
                          </span>
                          <span className=" font-lato font-bold">
                            {item.farmerPhone}
                          </span>{" "}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-4 border-b border-gray-200 flex-1">
                    <div className="space-y-6">
                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">timeConfirm</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.timeConfirm}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">problemInfo</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.problemInfo}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">solutionOffered</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.solutionOffered}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">productInfo</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.productInfo}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">featureOrBenefits</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.featureOrBenefits}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">responseHandle</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.responseHandle}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Body Language</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.bodyLanguage}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Visual Aids</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.visualAid}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">emotions</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.emotions}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">testimonials</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.testimonials}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">focused</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.focused}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">summaryClose</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.summaryClose}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">positiveFeed</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.positiveFeed}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">commitment Asked</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.commitmentAsked}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center ">
                        <span className="font-lato font-bold">Remarks</span>
                      </div>
                      <div className="flex items-center">
                        <p className="w-full break-words font-lato font-extrabold">
                          {item.remarks}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default FMFDCoaching;
